import React, { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { Badge, Button, Col, Container, Dropdown, Row } from "react-bootstrap";
import { FaCheck, FaPlay, FaPlus, FaStar, FaYoutube } from "react-icons/fa";
import { fetchCredits, fetchDetail, fetchSeason, fetchVideos } from "../../Api/Moviesapi";
import { ErrorState, LoadingState } from "../../Components/PageState/PageState";
import { useApp } from "../../context/AppContext";
import "./MovieDetail.css";

const IMAGE_BASE = "https://image.tmdb.org/t/p";
const STREAM_API_URL = "https://vidsrc.sbs/embed";

const createStreamUrl = ({ type, tmdbId, season, episode }) => {
  if (!STREAM_API_URL) return "";

  // Movie URL: REACT_APP_STREAM_API_URL/movie/{TMDB_ID}
  if (type === "movie") {
    return `${STREAM_API_URL}/movie/${tmdbId}`;
  }

  // TV URL: REACT_APP_STREAM_API_URL/tv/{TMDB_ID}/{SEASON}/{EPISODE}
  return `${STREAM_API_URL}/tv/${tmdbId}/${season}/${episode}`;
};

const selectTrailer = (videos) => {
  const youtube = videos.filter((video) => video.site === "YouTube" && video.key);
  return youtube.find((video) => video.type === "Trailer" && video.official)
    || youtube.find((video) => video.type === "Trailer")
    || youtube.find((video) => video.type === "Teaser")
    || null;
};

export default function MovieDetail() {
  const { type, id } = useParams();
  const { myList, toggleMyList } = useApp();
  const [data, setData] = useState(null);
  const [trailerKey, setTrailerKey] = useState("");
  const [cast, setCast] = useState([]);
  const [episodes, setEpisodes] = useState([]);
  const [season, setSeason] = useState(1);
  const [episode, setEpisode] = useState(1);
  const [showTrailer, setShowTrailer] = useState(false);
  const [showPlayer, setShowPlayer] = useState(false);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  useEffect(() => {
    let active = true;
    setLoading(true);
    setError("");
    setData(null);
    setTrailerKey("");
    setCast([]);
    setSeason(1);
    setEpisode(1);
    setShowTrailer(false);
    setShowPlayer(false);

    fetchDetail(type, id)
      .then(async (detail) => {
        if (!active) return;
        setData(detail);

        const [videosResult, creditsResult] = await Promise.allSettled([
          fetchVideos(type, id),
          fetchCredits(type, id),
        ]);
        if (!active) return;

        const videos = videosResult.status === "fulfilled" ? videosResult.value : [];
        const credits = creditsResult.status === "fulfilled" ? creditsResult.value : [];
        setTrailerKey(selectTrailer(videos)?.key || "");
        setCast(credits.slice(0, 12));
      })
      .catch((reason) => active && setError(reason.message || "Unable to load this title."))
      .finally(() => active && setLoading(false));

    return () => { active = false; };
  }, [id, type]);

  useEffect(() => {
    if (type !== "tv") {
      setEpisodes([]);
      return undefined;
    }

    let active = true;
    setEpisode(1);
    fetchSeason(id, season)
      .then((result) => active && setEpisodes(result.episodes || []))
      .catch(() => active && setEpisodes([]));
    return () => { active = false; };
  }, [id, season, type]);

  if (loading) return <LoadingState label="Loading title…" />;
  if (error || !data) return <ErrorState message={error || "Title not found."} />;

  const title = data.title || data.name || "Untitled";
  const poster = data.poster_path
    ? `${IMAGE_BASE}/w500${data.poster_path}`
    : "https://placehold.co/500x750?text=No+Poster";
  const backdropStyle = data.backdrop_path
    ? { backgroundImage: `linear-gradient(rgba(0,0,0,.6), #111), url(${IMAGE_BASE}/original${data.backdrop_path})` }
    : undefined;
  const saved = myList.some((item) => item.id === data.id && item.media_type === type);
  const streamUrl = createStreamUrl({
    type,
    tmdbId: id,
    season,
    episode,
  });

  return <div className="movie-detail-page" style={backdropStyle}>
    <Container className="movie-detail-content py-5">
      <Row className="g-4 align-items-start">
        <Col xs={12} md={4} lg={3}>
          <img src={poster} className="movie-poster" alt={`${title} poster`} />
        </Col>
        <Col xs={12} md={8} lg={9}>
          <h1>{title}</h1>
          {data.tagline && <p className="tagline">{data.tagline}</p>}
          <p className="movie-overview">{data.overview || "No overview is available."}</p>
          <div className="movie-meta">
            <span><FaStar color="gold" /> {Number(data.vote_average || 0).toFixed(1)}</span>
            <span>{data.release_date || data.first_air_date || "Date unavailable"}</span>
            {data.runtime > 0 && <span>{data.runtime} min</span>}
          </div>
          <div className="genre-list">{data.genres?.map((genre) => <Badge key={genre.id} bg="secondary">{genre.name}</Badge>)}</div>
          <div className="detail-actions">
            {trailerKey ? <Button variant="danger" onClick={() => { setShowTrailer((current) => !current); setShowPlayer(false); }}>
              <FaYoutube /> {showTrailer ? "Close Trailer" : "Play Trailer"}
            </Button> : <Button variant="secondary" disabled>Trailer unavailable</Button>}
            <Button variant="primary" onClick={() => { setShowPlayer((current) => !current); setShowTrailer(false); }}>
              <FaPlay /> {showPlayer ? "Close Player" : "Watch Now"}
            </Button>
            <Button variant="outline-light" onClick={() => toggleMyList({ ...data, media_type: type })}>
              {saved ? <FaCheck /> : <FaPlus />} {saved ? "Saved" : "My List"}
            </Button>
          </div>
          {type === "tv" && <div className="episode-selectors">
            <Dropdown onSelect={(value) => setSeason(Number(value))}>
              <Dropdown.Toggle variant="secondary">Season {season}</Dropdown.Toggle>
              <Dropdown.Menu>{Array.from({ length: data.number_of_seasons || 0 }, (_, index) => <Dropdown.Item key={index + 1} eventKey={index + 1}>Season {index + 1}</Dropdown.Item>)}</Dropdown.Menu>
            </Dropdown>
            <Dropdown onSelect={(value) => setEpisode(Number(value))}>
              <Dropdown.Toggle variant="secondary">Episode {episode}</Dropdown.Toggle>
              <Dropdown.Menu>{episodes.map((item) => <Dropdown.Item key={item.id || item.episode_number} eventKey={item.episode_number}>{item.episode_number}. {item.name}</Dropdown.Item>)}</Dropdown.Menu>
            </Dropdown>
          </div>}
        </Col>
      </Row>

      {showTrailer && trailerKey && <section className="trailer-section" aria-label={`${title} trailer`}>
        <div className="ratio ratio-16x9">
          <iframe src={`https://www.youtube-nocookie.com/embed/${encodeURIComponent(trailerKey)}?autoplay=1&rel=0`} title={`${title} official trailer`} allow="autoplay; encrypted-media; picture-in-picture; fullscreen" referrerPolicy="strict-origin-when-cross-origin" allowFullScreen />
        </div>
      </section>}

      {showPlayer && <section className="stream-section" aria-label={`${title} player`}>
        {streamUrl ? <div className="ratio ratio-16x9">
          <iframe src={streamUrl} title={`${title} player`} allow="autoplay; encrypted-media; picture-in-picture; fullscreen" referrerPolicy="strict-origin-when-cross-origin" allowFullScreen />
        </div> : <div className="stream-placeholder">
          <FaPlay size={36} />
          <h2>Streaming API ready to connect</h2>
          <p>Add <code>REACT_APP_STREAM_API_URL</code> to your <code>.env</code> file, then restart the app.</p>
        </div>}
      </section>}

      <section className="cast-section"><h2>Cast</h2>
        {cast.length ? <Row className="g-3">{cast.map((actor) => <Col key={actor.cast_id || actor.credit_id || actor.id} xs={6} sm={4} md={3} lg={2}>
          <article className="cast-card"><img src={actor.profile_path ? `${IMAGE_BASE}/w300${actor.profile_path}` : "https://placehold.co/300x450?text=No+Photo"} alt={actor.name} /><h3>{actor.name}</h3><p>{actor.character || "Cast"}</p></article>
        </Col>)}</Row> : <p className="text-secondary">Cast information is unavailable.</p>}
      </section>
    </Container>
  </div>;
}
